import { checkActiveSession, updateUser } from 'Features/Auth/authSlice';
import { fetchCompetences } from 'Features/Competence/competenceSlice';
import {
  DrivingLicenseType,
  fetchDrivingLicenses,
} from 'Features/DrivingLicense/drivingLicenseSlice';
import {
  MemberProfileLocation,
  MemberProfileProfessionalTitle,
  Recommender,
  createMemberProfile,
  getLanguages,
  updateMemberProfile,
} from 'Features/MemberProfile/memberProfileSlice';
import { fetchLocations } from 'Features/NewAdvertisement/newAdvertisementSlice';
import { fetchProfessionalTitleGroups } from 'Features/ProfessionalTitle/professionalTitleSlice';
import jobExperienceManager from 'Util/jobExperienceManager';
import {
  addDays,
  addMonths,
  addYears,
  differenceInMonths,
  formatISO,
} from 'date-fns';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components/macro';
import FormFields from './FormFields';
import FormTabs from './FormTabs';

export const getEndDate = (
  startYear: number,
  duration: number,
  durationType: string,
) => {
  const start = new Date(startYear, 1, 1);
  let date = start;
  switch (durationType) {
    case 'year':
      date = addYears(start, duration);
      break;
    case 'month':
      date = addMonths(start, duration);
      break;
    case 'day':
      date = addDays(start, duration);
      break;
    default:
      break;
  }
  return formatISO(date, { representation: 'date' });
};

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
const TitleArea = styled.div`
  display: flex;
  align-items: center;
  h3 {
    color: #252525;
    font-family: acumin-pro;
    font-size: 24px;
    letter-spacing: 0;
    line-height: 29px;
    font-weight: 200;
    margin: 0 0 0 15px;
  }
  span {
    border-radius: 6px;
    background-color: #D73714;
    color: #ffffff;
    font-family: acumin-pro;
    font-size: 20px;
    font-weight: bold;
    letter-spacing: 0;
    line-height: 25px;
    text-align: center;
    padding: 8px 15px;
    font-weight: 600;
    white-space: nowrap;
  }
`;
const Form = styled.div`
  background: #fff;
  flex-grow: 1;
  height: 100%;
  margin-top: 10px;
`;
const SkipForm = styled.div`
  text-align: center;
  margin-top: 16px;
  a {
    display: inline-block;
    color: #0a4ea2;
    margin-bottom: 10px;
  }
  p {
    color: #5c5f63;
    margin: 0;
  }
  a,
  p {
    font-family: acumin-pro;
    font-size: 14px;
    letter-spacing: 0;
    line-height: 17px;
  }
`;

const SurveyForm = ({
  formIndex,
  onFormIndexChange,
}: {
  formIndex: number;
  onFormIndexChange: Function;
}) => {
  const [values, setValues] = useState<{ [key: string]: any }>({});
  const [validationErrors, setValidationErrors] = useState<
    Record<string, boolean>
  >({});
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    user: { member_profile: profile, id: userId },
  } = useSelector((state: any) => state.auth);
  const { user, partialProfile } = useSelector((state: any) => state.auth);
  const history = useHistory();
  const location = useLocation();
  React.useEffect(() => {
    dispatch(checkActiveSession());
    dispatch(fetchProfessionalTitleGroups());
    dispatch(fetchCompetences());
    dispatch(fetchDrivingLicenses());
    dispatch(getLanguages());
    dispatch(fetchLocations());
  }, [dispatch]);
  const isIntern = values.is_intern === true;
  const phases = isIntern ? [0, 1, 4, 5] : [0, 1, 2, 3, 4, 5];
  const safeFormIndex = Math.min(formIndex, phases.length - 1);
  const currentPhase = phases[safeFormIndex];

  React.useEffect(() => {
    let newValues = {};
    if (profile) {
      newValues = {
        is_intern: profile.is_intern ?? false,
        possibility_to_use_own_car: profile.possibility_to_use_own_car,
        interested_in_household_employers:
          profile.interested_in_household_employers,
        drivingLicenses: profile.driving_licenses.map(
          (dl: DrivingLicenseType) => dl.id,
        ),
        competences: profile.competences.map(({ id }: { id: number }) => id),
        languages: profile.language_skills.map((lang: any) => ({
          id: lang.language.id,
          level: lang.level_of_competence,
        })),
        jobExperiences: profile.job_experiences.map((je: any) => {
          return jobExperienceManager(je);
        }),
        ownDescription: profile.additional_info,
        educations: profile.educations.map((ed: any) => ({
          educationInstitute: ed.education_institute,
          educationProgram: ed.education_program,
          educationDescription: ed.description,
          startYear: parseInt(ed.start_date.split('-')[0], 10),
          duration: differenceInMonths(
            new Date(ed.end_date),
            new Date(ed.start_date),
          ),
          durationType: 'month',
        })),
        professionalTitles: profile.professional_titles.map(
          (p: MemberProfileProfessionalTitle) => p.id,
        ),
        locations: profile.locations.map((p: MemberProfileLocation) => p.id),
        recommenders: profile.recommenders.map((r: Recommender) => ({
          name: r.name,
          phone: r.phone,
          email: r.email,
        })),
      };
    }

    if (partialProfile) {
      newValues = { ...newValues, ...partialProfile };
    }

    if (user) {
      newValues = {
        ...newValues,
        firstName: user.first_name,
        lastName: user.last_name,
        phone: user.phone,
        email: user.email,
      };
    }
    setValues(newValues);
  }, [profile, user, partialProfile]);
  const validatePage = (pageIndex: number): Record<string, boolean> => {
    const errors: Record<string, boolean> = {};
    switch (pageIndex) {
      case 0:
        if (values.is_intern === undefined || values.is_intern === null)
          errors.is_intern = true;
        break;
      case 1:
        if (!values.firstName?.trim()) errors.firstName = true;
        if (!values.lastName?.trim()) errors.lastName = true;
        if (!values.phone?.trim()) errors.phone = true;
        if (!values.email?.trim()) errors.email = true;
        break;
      case 2:
        if (!(values.professionalTitles || []).length)
          errors.professionalTitles = true;
        break;
      case 3:
        if (
          (values.jobExperiences || []).some(
            (je: any) =>
              !je.employer?.trim() ||
              !je.workAssignment?.trim() ||
              !je.startYear,
          )
        )
          errors.jobExperiences = true;
        break;
      case 4:
        if (
          (values.educations || []).some(
            (ed: any) =>
              !ed.educationInstitute?.trim() ||
              !ed.educationProgram?.trim() ||
              !ed.startYear,
          )
        )
          errors.educations = true;
        break;
      case 5:
        if (!(values.locations || []).length) errors.locations = true;
        {
          const filledRecommenders = (values.recommenders || []).filter(
            (r: any) => r.name || r.phone || r.email,
          );
          if (
            filledRecommenders.some(
              (r: any) =>
                !r.name?.trim() ||
                !r.phone?.trim() ||
                !r.email?.trim() ||
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email || ''),
            )
          )
            errors.recommenders = true;
        }
        break;
      default:
        break;
    }
    return errors;
  };

  const handleNavigation = (targetIndex: number) => {
    if (targetIndex > safeFormIndex) {
      const errors = validatePage(phases[safeFormIndex]);
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        toast.error(t('fill-missing-fields'));
        return;
      }
    }
    setValidationErrors({});
    onFormIndexChange(targetIndex);
  };

  const setMemberProfileValue = (key: string, value: string) => {
    const newState = { ...values };
    newState[key] = value;
    setValues(newState);
    if (validationErrors[key]) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };
  const renderTitle = () => {
    switch (currentPhase) {
      case 0:
        return t('title-section-role');
      case 1:
        return t('title-section-0');
      case 2:
        return t('title-section-1');
      case 3:
        return t('title-section-2');
      case 4:
        return t('title-section-3');
      case 5:
        return t('title-section-4');
      default:
        return t('title-section-role');
    }
  };

  const createOrUpdateProfile = async () => {
    const errors = validatePage(phases[phases.length - 1]);
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      toast.error(t('fill-missing-fields'));
      return;
    }
    const profileExists = profile !== null;
    const functionToUse: (body: any) => any = profileExists
      ? updateMemberProfile
      : createMemberProfile;
    const body = {
      is_open_profile: true,
      is_intern: values.is_intern ?? false,
      possibility_to_use_own_tools: false,
      possibility_to_use_own_car: values.possibility_to_use_own_car,
      interested_in_household_employers:
        values.interested_in_household_employers,
      driving_licenses: values.drivingLicenses,
      driving_license_ids: values.drivingLicenses,
      competence_ids: values.competences,
      language_skills: (values.languages || []).map((skill: any) => ({
        language_id: skill.id,
        level_of_competence: skill.level,
      })),
      job_experiences: (values.jobExperiences || []).map((je: any) => ({
        employer: je.employer,
        work_assignment: je.workAssignment,
        start_date: formatISO(
          new Date(je.startYear, (je.startMonth || 1) - 1, 1),
          {
            representation: 'date',
          },
        ),
        end_date: formatISO(
          new Date(je.endYear || je.startYear, (je.endMonth || 1) - 1, 1),
          {
            representation: 'date',
          },
        ),
        description: je.workDescription,
      })),
      educations: (values.educations || []).map((ed: any) => ({
        education_institute: ed.educationInstitute,
        education_program: ed.educationProgram,
        description: ed.educationDescription,
        start_date: formatISO(new Date(ed.startYear, 1, 1), {
          representation: 'date',
        }),
        end_date: getEndDate(ed.startYear, ed.duration, ed.durationType),
      })),
      additional_info: values.ownDescription,
      professional_title_ids: values.professionalTitles,
      recommenders: values.recommenders,
      location_ids: values.locations,
      user: user.id,
    };
    const requestBody = profile ? { id: profile.id, body } : body;
    const { error }: { error: Error } = await dispatch<any>(
      functionToUse(requestBody),
    );
    if (error) {
      const errorObj: { [key: string]: { [key: string]: any } } = JSON.parse(
        error.message,
      );
      const errors: Array<string> = [];
      Object.keys(errorObj).map((k: string) => {
        Object.keys(errorObj[k]).map((k2: string) => {
          const errorArray: Array<string> = errorObj[k][k2];
          if (k2.indexOf('date') !== -1) {
            if (errors.includes(`${t(k)} - ${t('date')} - ${errorArray[0]}`)) {
              return;
            }
            errors.push(`${t(k)} - ${t('date')} - ${errorArray[0]}`);
            return;
          }
          errors.push(`${t(k)} - ${t(k2)} - ${errorArray[0]}`);
        });
      });
      toast.error(
        <>
          <strong>{t('errors')}:</strong>
          {errors.map((e: string) => (
            <p>
              <strong>{e.split(' - ')[0]}</strong> - {e.split(' - ')[1]} -{' '}
              {e.split(' - ')[2]}
            </p>
          ))}
        </>,
      );
      return;
    }
    const { error: userError }: { error: Error } = await dispatch<any>(
      updateUser({
        id: userId,
        first_name: values.firstName,
        last_name: values.lastName,
        phone: values.phone,
        email: values.email,
      }),
    );
    if (userError) {
      const errorObj: { [key: string]: string } = JSON.parse(userError.message);
      const errors: Array<string> = [];
      Object.keys(errorObj).map((k: string) => {
        Object.keys(errorObj[k]).map((k2: string) => {
          const errorString: string = errorObj[k];
          if (k2.indexOf('date') !== -1) {
            if (errors.includes(`${t(k)} - ${t('date')} - ${errorString}`)) {
              return;
            }
            errors.push(`${t(k)} - ${t('date')} - ${errorString}`);
            return;
          }
          errors.push(`${t(k)} - ${errorString}`);
        });
      });
      toast.error(
        <>
          <strong>{t('errors')}:</strong>
          {errors.map((e: string) => (
            <p>
              <strong>{e.split(' - ')[0]}</strong> - {e.split(' - ')[1]}
            </p>
          ))}
        </>,
      );
      return;
    }
    toast.success(
      profileExists ? t('member-profile-updated') : t('member-profile-created'),
    );
    const callback = new URLSearchParams(location.search).get('callback');
    history.push(callback || '/jobs');
  };
  return (
    <FormContainer>
      <TitleArea>
        <span>
          {safeFormIndex + 1} / {phases.length}
        </span>
        <h3>{renderTitle()}</h3>
      </TitleArea>
      <Form>
        <FormTabs
          phases={phases}
          currentTab={safeFormIndex}
          onTabChange={(index: number) => handleNavigation(index)}
        />
        <FormFields
          currentTab={currentPhase}
          values={values}
          setValue={setMemberProfileValue}
          nextForm={() => handleNavigation(safeFormIndex + 1)}
          createProfile={createOrUpdateProfile}
          validationErrors={validationErrors}
        />
      </Form>
      {!profile && (
        <SkipForm>
          <Link to="/jobs">{t('skip-form-link-text')}</Link>
          <p>{t('skip-form-description')}</p>
        </SkipForm>
      )}
    </FormContainer>
  );
};

export default SurveyForm;
